import { boundedJson, RUNTIME_LIMITS } from '../../contracts/runtime-v2.mjs';
import { sdkErrorReply, sdkFailureMessage } from './tier0/error-presentation.mjs';

// A preview frame is opaque ('null'); a saved App connects from its own origin.
function validConnection(event, peer, scope, frameOrigin) {
  return (
    event.source === peer &&
    event.origin === frameOrigin &&
    event.data?.type === 'poneva.preview.connect' &&
    event.data.nonce === scope.documentNonce
  );
}

function validOperation(handlers, operation) {
  return (
    typeof operation === 'string' &&
    Object.hasOwn(handlers, operation) &&
    typeof handlers[operation]?.request === 'function'
  );
}

function validRequest(handlers, { id, operation }) {
  if (!validOperation(handlers, operation)) return false;
  return operation === 'runtime.ready' || (typeof id === 'string' && id.length <= 128);
}

// Existing private-preview port handshake, bound to the exact opaque iframe
// and saved document nonce. Authority stays at the authenticated host.
export function createOpaquePreviewTransport({
  window,
  peer,
  scope,
  handlers,
  frameOrigin = 'null',
}) {
  let active = true,
    port = null,
    pending = 0,
    epoch = Date.now(),
    received = 0;
  const invalidations = new Set();
  const subscriptions = new Map();
  const stopSubscriptions = () => {
    for (const dispose of subscriptions.values())
      try {
        dispose();
      } catch {
        /* Retire all observers. */
      }
    subscriptions.clear();
  };
  let reads = new AbortController();
  const currentPort = (ownedPort) => active && ownedPort === port;
  const respond = (ownedPort, response) => {
    if (currentPort(ownedPort) && response.id) ownedPort.postMessage(response);
  };
  // A classified data read failure carries its numbers (time used, time allowed)
  // so the saved App can say what happened, as in the private preview.
  const respondError = (ownedPort, id, error) => {
    if (!currentPort(ownedPort) || !id) return;
    const details = error?.details;
    ownedPort.postMessage({
      id,
      ...sdkErrorReply(error),
      ...(details && typeof details === 'object' && !Array.isArray(details)
        ? { detail: details }
        : {}),
    });
  };
  const withinLimits = () => {
    const now = Date.now();
    if (now - epoch >= 1000) {
      epoch = now;
      received = 0;
    }
    return !(
      ++received > RUNTIME_LIMITS.messagesPerSecond ||
      pending + subscriptions.size >= RUNTIME_LIMITS.pending
    );
  };
  function subscriptionKind(message) {
    if (
      message.operation !== 'runtime.cached-view' ||
      typeof message.id !== 'string' ||
      message.id.length > 128
    )
      return null;
    return message.type;
  }
  function keepSubscription(ownedPort, id, dispose) {
    if (typeof dispose !== 'function') throw Error('invalid_subscription');
    if (!currentPort(ownedPort)) {
      dispose();
      return;
    }
    subscriptions.set(id, dispose);
    respond(ownedPort, { id, result: { subscribed: true } });
  }
  function stopSubscription(id) {
    subscriptions.get(id)?.();
    subscriptions.delete(id);
  }
  async function cachedSubscription(ownedPort, message) {
    const { id, operation, type } = message;
    if (!subscriptionKind(message)) return;
    if (type === 'unsubscribe') {
      stopSubscription(id);
      return;
    }
    if (type !== 'subscribe' || subscriptions.has(id)) return;
    if (typeof handlers[operation]?.subscribe !== 'function') {
      respondError(
        ownedPort,
        id,
        Object.assign(Error('LIVE_UNAVAILABLE'), { code: 'LIVE_UNAVAILABLE' }),
      );
      return;
    }
    if (!withinLimits()) return invalidate();
    pending++;
    try {
      const dispose = await handlers[operation].subscribe(
        boundedJson(message.input ?? {}),
        (value) => respond(ownedPort, { id, operation, type: 'event', result: boundedJson(value) }),
      );
      keepSubscription(ownedPort, id, dispose);
    } catch (error) {
      respondError(ownedPort, id, error);
    } finally {
      pending--;
    }
  }
  async function dispatch(ownedPort, event) {
    if (!currentPort(ownedPort)) return;
    if (['subscribe', 'unsubscribe'].includes(event.data?.type))
      return cachedSubscription(ownedPort, event.data);
    const { id, operation, input } = event.data ?? {};
    if (!validRequest(handlers, { id, operation })) return;
    if (!withinLimits()) return invalidate();
    pending++;
    try {
      const result = await handlers[operation].request(boundedJson(input ?? {}), {
        signal: reads.signal,
      });
      respond(ownedPort, {
        id,
        result,
        ...(sdkFailureMessage(result) ? { message: sdkFailureMessage(result) } : {}),
      });
    } catch (error) {
      respondError(ownedPort, id, error);
    } finally {
      pending--;
    }
  }
  const receive = (event) => {
    if (!active || !validConnection(event, peer, scope, frameOrigin)) return;
    const pair = new MessageChannel();
    reads.abort();
    stopSubscriptions();
    reads = new AbortController();
    port?.close();
    port = pair.port1;
    const ownedPort = port;
    port.onmessage = (event) => dispatch(ownedPort, event);
    peer.postMessage({ type: 'poneva.preview.connect', nonce: scope.documentNonce }, '*', [
      pair.port2,
    ]);
  };
  const invalidate = () => {
    if (!active) return;
    active = false;
    reads.abort();
    stopSubscriptions();
    port?.close();
    window.removeEventListener('message', receive);
    for (const fn of invalidations) fn();
    invalidations.clear();
  };
  window.addEventListener('message', receive);
  return Object.freeze({
    invalidate,
    onInvalidate(fn) {
      invalidations.add(fn);
      return () => invalidations.delete(fn);
    },
  });
}
